import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { adminCacheService } from '@/core/services/AdminCacheService';
import { optimizedStorageService } from '@/core/services/OptimizedStorageService';
import { AdminService } from '@/core/services/AdminService';
import { debugLogger } from '@/utils/debugLogger';
import { AdminUserFilters } from './useAdminFilters';
import { UserTypeFilter } from './usePaginatedMediaData';

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  pendingVerifications: number;
  totalConnections: number;
  totalMessages: number;
  onlineUsers: number;
}

interface UseOptimizedAdminDataReturn {
  stats: DashboardStats;
  users: any[];
  verifications: any[];
  storageData: Record<string, any>;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  clearCache: () => void;
  loadMoreUsers: () => Promise<void>;
  hasMoreUsers: boolean;
  loadingMore: boolean;
}

const USERS_PER_PAGE = 50;
const MAX_USERS_LOAD_ALL = 500; // Se o total for menor que isso, carrega todos de uma vez

interface UseOptimizedAdminDataParams {
  filters?: AdminUserFilters;
  userTypeFilter?: UserTypeFilter;
}

export function useOptimizedAdminData(params?: UseOptimizedAdminDataParams): UseOptimizedAdminDataReturn {
  const { filters, userTypeFilter = 'all' } = params || {};
  const [users, setUsers] = useState<any[]>([]);
  const [storageData, setStorageData] = useState<Record<string, any>>({});
  const [error, setError] = useState<string | null>(null);
  const [usersPage, setUsersPage] = useState(0);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Optimized stats query with real data
  const { 
    data: stats = {
      totalUsers: 0,
      activeUsers: 0,
      pendingVerifications: 0,
      totalConnections: 0,
      totalMessages: 0,
      onlineUsers: 0
    }, 
    isLoading: statsLoading,
    refetch: refetchStats 
  } = useQuery({
    queryKey: ['admin-stats-real', userTypeFilter],
    queryFn: async () => {
      debugLogger.debug('[AdminData] Fetching real stats from database', { userTypeFilter });
      
      // Build query based on userTypeFilter
      let totalUsersQuery = supabase.from('profiles_public').select('*', { count: 'exact', head: true });
      let activeUsersQuery = supabase.from('profiles_public').select('*', { count: 'exact', head: true }).eq('onboarding_completed', true);
      let onlineUsersQuery = supabase.from('profiles_public').select('*', { count: 'exact', head: true }).eq('is_online', true);

      // Apply userTypeFilter to queries - excluir teste de todos exceto 'test'
      switch (userTypeFilter) {
        case 'test':
          totalUsersQuery = totalUsersQuery.ilike('username', 'test-%');
          activeUsersQuery = activeUsersQuery.ilike('username', 'test-%');
          onlineUsersQuery = onlineUsersQuery.ilike('username', 'test-%');
          break;
        case 'sponsors':
          totalUsersQuery = totalUsersQuery.eq('role', 'sponsor').not('username', 'ilike', 'test-%');
          activeUsersQuery = activeUsersQuery.eq('role', 'sponsor').not('username', 'ilike', 'test-%');
          onlineUsersQuery = onlineUsersQuery.eq('role', 'sponsor').not('username', 'ilike', 'test-%');
          break;
        case 'sponsored':
          totalUsersQuery = totalUsersQuery.eq('role', 'sponsored').not('username', 'ilike', 'test-%');
          activeUsersQuery = activeUsersQuery.eq('role', 'sponsored').not('username', 'ilike', 'test-%');
          onlineUsersQuery = onlineUsersQuery.eq('role', 'sponsored').not('username', 'ilike', 'test-%');
          break;
        case 'free':
          totalUsersQuery = totalUsersQuery.in('plan_type', ['free', 'sponsor_free', 'sponsored_free']).not('username', 'ilike', 'test-%');
          activeUsersQuery = activeUsersQuery.in('plan_type', ['free', 'sponsor_free', 'sponsored_free']).not('username', 'ilike', 'test-%');
          onlineUsersQuery = onlineUsersQuery.in('plan_type', ['free', 'sponsor_free', 'sponsored_free']).not('username', 'ilike', 'test-%');
          break;
        case 'standard':
          totalUsersQuery = totalUsersQuery.eq('plan_type', 'sponsor_standard').not('username', 'ilike', 'test-%');
          activeUsersQuery = activeUsersQuery.eq('plan_type', 'sponsor_standard').not('username', 'ilike', 'test-%');
          onlineUsersQuery = onlineUsersQuery.eq('plan_type', 'sponsor_standard').not('username', 'ilike', 'test-%');
          break;
        case 'premium':
          totalUsersQuery = totalUsersQuery.in('plan_type', ['sponsor_premium', 'sponsored_premium']).not('username', 'ilike', 'test-%');
          activeUsersQuery = activeUsersQuery.in('plan_type', ['sponsor_premium', 'sponsored_premium']).not('username', 'ilike', 'test-%');
          onlineUsersQuery = onlineUsersQuery.in('plan_type', ['sponsor_premium', 'sponsored_premium']).not('username', 'ilike', 'test-%');
          break;
        case 'active':
          totalUsersQuery = totalUsersQuery.eq('onboarding_completed', true).not('username', 'ilike', 'test-%');
          activeUsersQuery = activeUsersQuery.eq('onboarding_completed', true).not('username', 'ilike', 'test-%');
          onlineUsersQuery = onlineUsersQuery.eq('onboarding_completed', true).not('username', 'ilike', 'test-%');
          break;
        case 'pending':
          totalUsersQuery = totalUsersQuery.eq('onboarding_completed', false).not('username', 'ilike', 'test-%');
          activeUsersQuery = activeUsersQuery.eq('onboarding_completed', false).not('username', 'ilike', 'test-%');
          onlineUsersQuery = onlineUsersQuery.eq('onboarding_completed', false).not('username', 'ilike', 'test-%');
          break;
        case 'hidden':
          totalUsersQuery = totalUsersQuery.eq('hidden', true).not('username', 'ilike', 'test-%');
          activeUsersQuery = activeUsersQuery.eq('hidden', true).not('username', 'ilike', 'test-%');
          onlineUsersQuery = onlineUsersQuery.eq('hidden', true).not('username', 'ilike', 'test-%');
          break;
        case 'admins':
          // Para stats, buscar IDs de admin/moderator
          const { data: roleData } = await supabase
            .from('user_roles')
            .select('user_id')
            .in('role', ['admin', 'moderator']);
          const adminIds = roleData?.map(r => r.user_id) || [];
          if (adminIds.length > 0) {
            totalUsersQuery = totalUsersQuery.in('id', adminIds).not('username', 'ilike', 'test-%');
            activeUsersQuery = activeUsersQuery.in('id', adminIds).not('username', 'ilike', 'test-%');
            onlineUsersQuery = onlineUsersQuery.in('id', adminIds).not('username', 'ilike', 'test-%');
          } else {
            // Se não há admins, retornar vazio
            totalUsersQuery = totalUsersQuery.eq('id', '00000000-0000-0000-0000-000000000000');
            activeUsersQuery = activeUsersQuery.eq('id', '00000000-0000-0000-0000-000000000000');
            onlineUsersQuery = onlineUsersQuery.eq('id', '00000000-0000-0000-0000-000000000000');
          }
          break;
        case 'all':
        default:
          // Excluir contas de teste do "Todos"
          totalUsersQuery = totalUsersQuery.not('username', 'ilike', 'test-%');
          activeUsersQuery = activeUsersQuery.not('username', 'ilike', 'test-%');
          onlineUsersQuery = onlineUsersQuery.not('username', 'ilike', 'test-%');
          break;
      }
      
      const [
        totalUsersCountResult,
        activeUsersCountResult,
        onlineUsersCountResult,
        connectionsCountResult,
        messagesCountResult,
        verificationsResult
      ] = await Promise.allSettled([
        totalUsersQuery,
        activeUsersQuery,
        onlineUsersQuery,
        supabase.from('connections').select('*', { count: 'exact', head: true }),
        supabase.from('messages').select('*', { count: 'exact', head: true }),
        supabase.from('verification_status').select('id, status').eq('status', 'waiting')
      ]);

      const totalUsersCount = totalUsersCountResult.status === 'fulfilled' ? totalUsersCountResult.value.count || 0 : 0;
      const activeUsersCount = activeUsersCountResult.status === 'fulfilled' ? activeUsersCountResult.value.count || 0 : 0;
      const onlineUsersCount = onlineUsersCountResult.status === 'fulfilled' ? onlineUsersCountResult.value.count || 0 : 0;
      const connectionsCount = connectionsCountResult.status === 'fulfilled' ? connectionsCountResult.value.count || 0 : 0;
      const messagesCount = messagesCountResult.status === 'fulfilled' ? messagesCountResult.value.count || 0 : 0;
      const pendingVerifications = verificationsResult.status === 'fulfilled' ? verificationsResult.value.data || [] : [];

      const statsData = {
        totalUsers: totalUsersCount,
        activeUsers: activeUsersCount,
        pendingVerifications: pendingVerifications.length,
        totalConnections: connectionsCount,
        totalMessages: messagesCount,
        onlineUsers: onlineUsersCount
      };

      debugLogger.debug('[AdminData] Real stats calculated:', statsData);
      return statsData;
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    refetchIntervalInBackground: false,
  });

  // Load users with pagination and filters
  const loadUsers = useCallback(async (isLoadMore = false) => {
    try {
      // Verificar permissão de admin ANTES de chamar RPC
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) {
        debugLogger.debug('[AdminData] No authenticated user, skipping load');
        return;
      }

      const { data: hasAccess } = await supabase.rpc('can_access_admin_panel', { user_id_param: currentUser.id });
      if (!hasAccess) {
        debugLogger.debug('[AdminData] User lacks admin access, skipping RPC call');
        return;
      }

      if (isLoadMore) {
        setLoadingMore(true);
      }

      const offset = isLoadMore ? usersPage * USERS_PER_PAGE : 0;
      
      // FIX 3: Se o total de usuários for menor que MAX_USERS_LOAD_ALL, carrega todos de uma vez
      const effectiveLimit = stats.totalUsers > 0 && stats.totalUsers < MAX_USERS_LOAD_ALL 
        ? stats.totalUsers 
        : USERS_PER_PAGE;

      debugLogger.debug('[AdminData] Calling RPC get_admin_users_with_emails_paginated', {
        p_limit: effectiveLimit,
        p_offset: offset,
        userTypeFilter,
        totalUsers: stats.totalUsers,
        filters: {
          planTypes: filters?.planTypes,
          gender: filters?.gender,
          orientation: filters?.orientation,
          role: filters?.role,
          verificationLevel: filters?.verificationLevel,
          onboardingStatus: filters?.onboardingStatus,
          hiddenStatus: filters?.hiddenStatus,
          createdDateRange: filters?.createdDateRange,
          hasPhotos: filters?.hasPhotos,
          searchTerm: filters?.searchTerm,
          userRoles: filters?.userRoles
        }
      });

      const { data, error } = await supabase.rpc('get_admin_users_with_emails_paginated', {
        p_limit: effectiveLimit,
        p_offset: offset,
        p_search_term: filters?.searchTerm || null,
        p_plan_types: filters?.planTypes?.length ? filters.planTypes : null,
        p_gender: filters?.gender?.length ? filters.gender : null,
        p_orientation: filters?.orientation?.length ? filters.orientation : null,
        p_role: filters?.role?.length ? filters.role : null,
        p_verification_level: filters?.verificationLevel?.length ? filters.verificationLevel : null,
        p_onboarding_completed: filters?.onboardingStatus?.includes('completed') 
          ? true 
          : filters?.onboardingStatus?.includes('incomplete') 
          ? false 
          : null,
        p_hidden: filters?.hiddenStatus?.includes('hidden') 
          ? true 
          : filters?.hiddenStatus?.includes('visible') 
          ? false 
          : null,
        p_created_after: filters?.createdDateRange?.start?.toISOString() || null,
        p_created_before: filters?.createdDateRange?.end?.toISOString() || null,
        p_user_type_filter: userTypeFilter || 'all',
        p_has_photos: filters?.hasPhotos ?? null,
        p_user_roles: filters?.userRoles?.length ? filters.userRoles : null
      });

      if (error) {
        debugLogger.error('[AdminData] Error loading users', error);
        setError('Erro ao carregar usuários');
        return;
      }

      // Backend now handles all filtering via p_user_type_filter
      const filteredData: any[] = data || [];
      
      debugLogger.debug('[AdminData] Users loaded from backend', {
        userTypeFilter,
        userCount: filteredData.length,
        sampleUsernames: filteredData.slice(0, 5).map((u: any) => u.username)
      });

      // O RPC já retorna user_role, não precisa buscar novamente
      const usersWithRoles = filteredData;
      
      // Pré-popular cache do AdminService com os dados recebidos
      AdminService.preloadRolesCache(usersWithRoles);

      debugLogger.debug('[AdminData] Users loaded with roles from backend', {
        userTypeFilter,
        userCount: usersWithRoles.length,
        sampleRoles: usersWithRoles.slice(0, 3).map((u: any) => ({ 
          username: u.username, 
          role: u.user_role 
        }))
      });

      if (isLoadMore) {
        setUsers(prev => [...prev, ...usersWithRoles]);
        setUsersPage(prev => prev + 1);
      } else {
        setUsers(usersWithRoles);
        setUsersPage(1);
      }

      // Se carregamos todos de uma vez (effectiveLimit >= totalUsers), não há mais usuários
      const loadedAll = stats.totalUsers > 0 && stats.totalUsers < MAX_USERS_LOAD_ALL;
      setHasMoreUsers(!loadedAll && (filteredData?.length || 0) === USERS_PER_PAGE);
      
      debugLogger.debug('[AdminData]', `Loaded ${filteredData?.length || 0} users with roles (offset: ${offset})`);
    } catch (err) {
      debugLogger.error('[AdminData] Exception loading users', err);
      setError('Erro ao carregar usuários');
    } finally {
      if (isLoadMore) {
        setLoadingMore(false);
      }
    }
  }, [usersPage, filters, userTypeFilter, stats.totalUsers]);

  // Ref to always have latest loadUsers version (prevents stale closure in useEffect)
  const loadUsersRef = useRef(loadUsers);
  useEffect(() => {
    loadUsersRef.current = loadUsers;
  }, [loadUsers]);

  // Load storage data
  const loadStorageData = useCallback(async (forceRefresh = false) => {
    try {
      debugLogger.debug('[AdminData] Loading real storage data');
      const buckets = ['chat_media', 'profile_photos'];
      
      const data = await optimizedStorageService.getAllBucketsComplete(buckets, forceRefresh);
      
      const transformedData: Record<string, any> = {};
      Object.entries(data).forEach(([bucket, files]) => {
        transformedData[bucket] = {
          files,
          hasMore: false,
          total: files.length
        };
      });
      
      setStorageData(transformedData);
      debugLogger.debug('[AdminData] Loaded real storage data:', Object.keys(transformedData));
    } catch (err) {
      debugLogger.error('[AdminData] Error loading storage:', err);
      setError('Erro ao carregar dados de armazenamento');
    }
  }, []);

  // Initial load - reset page when filters change
  useEffect(() => {
    debugLogger.debug('[AdminData] Filters changed, resetting and reloading users', { 
      filters, 
      userTypeFilter 
    });
    setUsersPage(0);
    setUsers([]);
    // Use ref to always call latest version of loadUsers (fixes stale closure)
    loadUsersRef.current(false);
    loadStorageData();
  }, [filters, userTypeFilter, loadStorageData]);

  const loadMoreUsers = useCallback(async () => {
    if (!loadingMore && hasMoreUsers) {
      await loadUsers(true);
    }
  }, [loadUsers, loadingMore, hasMoreUsers]);

  const refetch = useCallback(async () => {
    setError(null);
    setUsersPage(0);
    await Promise.all([
      refetchStats(),
      loadUsers(false),
      loadStorageData(true)
    ]);
  }, [refetchStats, loadUsers, loadStorageData]);

  const clearCache = useCallback(() => {
    adminCacheService.clear();
    optimizedStorageService.clearCache();
  }, []);

  const loading = statsLoading;

  return {
    stats,
    users,
    verifications: [],
    storageData,
    loading,
    error,
    refetch,
    clearCache,
    loadMoreUsers,
    hasMoreUsers,
    loadingMore
  };
}
